import styled from 'styled-components';
import { useState } from 'react';
import { useDebouncedState } from '@react-hookz/web';

import { Location } from '../../../../features/location/Location';

import { suggestedSearches } from '../../../../components/PageWrappers/SharedComponents/GlobalSearchModal/suggestedSearches';
import { BaseSearch } from '../../../../components/Toolkit/SearchModal/BaseSearch';
import { InputButton } from '../../../../components/Toolkit/InputButton/InputButton';
import { SearchModal } from '../../../../components/Toolkit/SearchModal/SearchModal';
import { useAutoCompleteValues } from '../../Layouts/queries/useAutoCompleteValues';

import { useLocationWriteState } from '../../../../features/location/hooks/LocationWriteState/LocationWriteState.hook';

import type { IHandleSelectSearchItem } from '../../../../components/Toolkit/SearchModal/BaseSearch';

import { Desktop } from '../../../../components/Support/Desktop/Desktop';
import { Mobile } from '../../../../components/Support/Mobile/Mobile';

import type { DisplayContainerConfig } from '../../../../components/Support/DisplayContainer/DisplayContainer';
import { PAGE } from '../../../../helpers/pages';
import { HomePageSearchButton } from '../../Layouts/HomePageSearchManager.styled';
import { VERTICAL, VERTICAL_LABELS } from '../../../../helpers/verticals';

import { constructUrlParams } from '../../../../helpers/formatting';
import { useOnUpdateOnly } from '../../../../hooks/UseOnUpdateOnly';

import {
  generateSearchDescription,
  formatSearchItemTerms,
} from '../../../../helpers/formatSearchTerms';
import { useLocationContext } from '../../../../features/location/Location.context';
import { DEFAULT_PRESELECT } from '../../../../features/location/Location.constants';
import { handlePushToDataLayerForLocationSearch } from '../../../../features/location/helpers/Tracking';
import { restoreScrollPosition } from '../../../../helpers/scroll';
import { useLastSearch } from '../../../../components/PageWrappers/SharedComponents/LastSearchCard/hooks/useLastSearch';

export interface VerticalSearchProps {
  baseUrl: string;
  lastSearchDataTrackingValue?: string;
  vertical: VERTICAL;
}

const Container = styled.div`
  display: grid;
  grid-template-columns: auto;
  grid-template-rows: 48px auto;
  gap: ${({ theme }) => theme.spacing.M16};
  margin-bottom: ${({ theme }) => theme.spacing.M16};
`;

function VerticalSearch({
  baseUrl,
  vertical,
  lastSearchDataTrackingValue,
}: VerticalSearchProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchModalOpen, setIsSearchModalOpen] = useState<boolean>(false);
  const [searchQueryDebounced, setSearchQueryDebounced] = useDebouncedState(
    searchQuery,
    200,
  );
  const autoCompleteResponse = useAutoCompleteValues(
    searchQueryDebounced,
    PAGE.HOME,
  );
  const { lastSearchData, setLastSearch } = useLastSearch();
  const { handleSelectListItem: onSelect } = useLocationWriteState();

  const bottomSheetConfig: DisplayContainerConfig = {
    small: 'block',
    medium: 'none',
    large: 'none',
  };
  const dropdownConfig: DisplayContainerConfig = {
    small: 'none',
    medium: 'block',
    large: 'block',
  };

  const handleSearchInputChange = (newText: string) => {
    setSearchQuery(newText);
    setSearchQueryDebounced(newText);
  };

  const { selectedCounty, countyTown, coordinates, radius, resetToDefault } =
    useLocationContext();

  useOnUpdateOnly(() => {
    resetToDefault();
  }, [vertical]);

  const handleVerticalSearch = (
    verticalQuery: string,
    section: string,
    sectionDisplayName: string,
  ) => {
    let verticalParams;
    let locationQuery;

    if (
      selectedCounty !== null &&
      selectedCounty?.value !== DEFAULT_PRESELECT.value
    ) {
      const { latitude, longitude } = coordinates;
      verticalParams = constructUrlParams({
        words: verticalQuery,
        area: selectedCounty.value,
        countyTown:
          Boolean(latitude && longitude) && countyTown ? countyTown : undefined,
        radius: Boolean(latitude && longitude) ? radius : undefined,
      });
      locationQuery = `${selectedCounty.displayName}${
        countyTown ? `, ${countyTown} (+${radius}km)` : ''
      }`;
    } else {
      verticalParams = constructUrlParams({
        words: verticalQuery,
      });
    }

    const verticalUrl = `${baseUrl}/${section}${
      verticalParams ? `?${verticalParams}` : ''
    }`;

    if (verticalQuery || locationQuery) {
      const formattedVerticalQuery = verticalQuery
        ? ` · '${verticalQuery}'`
        : '';
      const formattedLocationQuery = locationQuery ? ` · ${locationQuery}` : '';
      setLastSearch(
        `${sectionDisplayName}${formattedVerticalQuery}${formattedLocationQuery}`,
        verticalUrl,
      );
    }
    if (selectedCounty?.displayName) {
      handlePushToDataLayerForLocationSearch({
        county: selectedCounty?.displayName,
        countyTown,
        radius,
      });
    }

    restoreScrollPosition(`${verticalUrl.replace(baseUrl, '')}`);

    location.assign(verticalUrl);
  };

  function handleSearch() {
    switch (vertical) {
      case VERTICAL.MARKETPLACE:
        handleVerticalSearch(searchQuery, 'all', 'All Sections');
        break;
      case VERTICAL.FARMING:
        handleVerticalSearch(searchQuery, 'farming', 'Farming');
        break;
    }
  }

  function handleSelectSearchItem({
    autoSuggestedText,
    searchRequest,
    url,
    category,
  }: IHandleSelectSearchItem) {
    let urlParams;
    const description = generateSearchDescription(
      searchRequest?.makeModelFilters,
      autoSuggestedText,
    );
    if (
      selectedCounty !== null &&
      selectedCounty?.value !== DEFAULT_PRESELECT.value
    ) {
      const { latitude, longitude } = coordinates;
      urlParams = `${url?.includes('?') ? '&' : '?'}${constructUrlParams({
        area: selectedCounty.value,
        countyTown: countyTown ?? undefined,
        radius: Boolean(latitude && longitude) ? radius : undefined,
      })}`;
    }
    let formattedUrl: string;
    if (url) {
      if (vertical === VERTICAL.MOTOR) {
        formattedUrl = `${url.replace('all', VERTICAL.FARMING)}${
          urlParams ?? ''
        }`;
      } else {
        formattedUrl = `${url}${urlParams ?? ''}`;
      }
    } else {
      formattedUrl = `${baseUrl}/${
        category ?? (vertical === VERTICAL.FARMING ? VERTICAL.FARMING : 'all')
      }?words=${autoSuggestedText}${urlParams ?? ''}`;
    }

    restoreScrollPosition(`${formattedUrl.replace(baseUrl, '')}`);

    location.assign(formattedUrl);
    setLastSearch(
      formatSearchItemTerms(description, category, autoSuggestedText),
      formattedUrl,
    );
  }

  const searchText = `Search ${VERTICAL_LABELS[vertical]}`;

  return (
    <>
      <Container>
        <Mobile>
          <InputButton
            isActive={isSearchModalOpen}
            value={searchQuery}
            onClick={() => setIsSearchModalOpen(true)}
            placeholder={searchText}
            variant="SEARCH"
          />
        </Mobile>
        <Desktop>
          <BaseSearch
            variant="INLINE"
            onInput={handleSearchInputChange}
            onHandleSearch={handleSearch}
            onHandleSelectSearchItem={handleSelectSearchItem}
            initialSearchValues={suggestedSearches(baseUrl)}
            placeholder={searchText}
            autoSuggestItems={autoCompleteResponse.data?.suggestions ?? []}
            lastSearchData={lastSearchData}
            lastSearchDataTrackingValue={lastSearchDataTrackingValue}
          />
        </Desktop>
        <Location
          skeletonListLength={16}
          maxHeight="376px"
          secondarySelectWidth="116px"
          maxTextContainerWidthMWeb="120px"
          onSelect={onSelect}
          bottomSheetConfig={bottomSheetConfig}
          dropdownConfig={dropdownConfig}
          stackVertically
          verticalGap="M16"
          heightVariant="LARGE"
        />
        {isSearchModalOpen && (
          <SearchModal
            askToClose={() => setIsSearchModalOpen(false)}
            isOpen={isSearchModalOpen}
            placeholder={searchText}
            presetInput={searchQuery}
            onInput={handleSearchInputChange}
            onSearch={handleSearch}
            onHandleSelectSearchItem={handleSelectSearchItem}
            initialSearchValues={suggestedSearches(baseUrl)}
            autoSuggestItems={autoCompleteResponse.data?.suggestions ?? []}
            lastSearchData={lastSearchData}
          />
        )}
      </Container>
      <HomePageSearchButton
        size="LARGE"
        onClick={handleSearch}
        data-testid="homepage-search-button"
      >
        {searchText}
      </HomePageSearchButton>
    </>
  );
}

export { VerticalSearch };
